import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ArrowLeft, X, ChevronDown,
  Music, Camera, Plane, Video, Activity, Palette, Mountain, Book, PersonStanding, Flag as FlagIcon, ChefHat, Gamepad2, LayoutGrid,
  MapPin, Car, Globe,
  Gem, Dumbbell, Utensils, TreePine, Wine, Flower2, PawPrint, Calendar
} from "lucide-react";
import Flag from "react-world-flags";
import { Country } from "country-state-city";
import { useDispatch } from "react-redux";
import styles from "./Step4_2LifestyleProfile.module.css";
import parentStyles from "../CreatorApplication.module.css";
import { editProfileDataV2API } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { setUser } from "../../../../redux/modules/userSlice";
import { formatDOBToYYYYMMDD } from "../../MemberOnboarding/steps/Step4MemberProfile";
import { USER_TYPE_IDS } from "../../../Header/ProfileViewer/profile-viewer";

interface Step4_2Props {
  onNext: () => void;
  onBack: () => void;
  profileData?: Record<string, any>;
  canGoBack?: boolean;
}

const HOBBIES = [
  { id: "music", label: "Music", Icon: Music },
  { id: "photography", label: "Photography", Icon: Camera },
  { id: "travel", label: "Travel", Icon: Plane },
  { id: "tiktok", label: "TikTok", Icon: Video },
  { id: "dance", label: "Dance", Icon: Activity },
  { id: "painting", label: "Painting", Icon: Palette },
  { id: "hiking", label: "Hiking", Icon: Mountain },
  { id: "reading", label: "Reading", Icon: Book },
  { id: "yoga", label: "Yoga", Icon: PersonStanding },
  { id: "golf", label: "Golf", Icon: FlagIcon },
  { id: "cooking", label: "Cooking", Icon: ChefHat },
  { id: "gaming", label: "Gaming", Icon: Gamepad2 },
  { id: "other_hobby", label: "Other", Icon: LayoutGrid },
];

const TRAVEL = [
  { id: "local", label: "Local Only", Icon: MapPin },
  { id: "state", label: "Within My State", Icon: Car },
  { id: "nationwide", label: "Nationwide", Icon: FlagIcon },
  { id: "international", label: "International", Icon: Globe },
  { id: "open", label: "Open to Travel", Icon: Plane },
];

const PREFERENCES = [
  { id: "luxury", label: "Luxury Lifestyle", Icon: Gem },
  { id: "active", label: "Active Lifestyle", Icon: Dumbbell },
  { id: "dining", label: "Fine Dining", Icon: Utensils },
  { id: "outdoor", label: "Outdoor Adventures", Icon: TreePine },
  { id: "nightlife", label: "Nightlife", Icon: Wine },
  { id: "wellness", label: "Wellness Focused", Icon: Flower2 },
  { id: "pet", label: "Pet Friendly", Icon: PawPrint },
  { id: "social", label: "Social Events", Icon: Calendar },
  { id: "other_pref", label: "Other", Icon: LayoutGrid },
];

export default function Step4_2LifestyleProfile({ onNext, onBack, profileData, canGoBack = true }: Step4_2Props) {
  const dispatch = useDispatch();
  const [interest, setInterest] = useState<string>(profileData?.interests || "women");
  const [hobbies, setHobbies] = useState<string[]>(profileData?.hobbies || ["music", "dance", "yoga"]);
  const [travel, setTravel] = useState<string>(profileData?.travelAvailability?.[0] || "local");
  const [destinations, setDestinations] = useState<string[]>(() => {
    const init = profileData?.favouriteDestinations;
    if (Array.isArray(init)) return init.slice(0, 5);
    if (typeof init === "string") {
      try {
        const parsed = JSON.parse(init);
        if (Array.isArray(parsed)) return parsed.slice(0, 5);
      } catch {
        return [init];
      }
    }
    return [];
  });
  const [destInput, setDestInput] = useState("");
  const [destError, setDestError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [preferences, setPreferences] = useState<string[]>(profileData?.lifeStylePreference || ["luxury", "nightlife", "wellness"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const filteredCountries = useMemo(() => {
    if (!destInput.trim()) {
      return allCountries.slice(0, 20);
    }
    const query = destInput.toLowerCase().trim();
    return allCountries
      .filter(
        c =>
          c.name.toLowerCase().includes(query) ||
          c.isoCode.toLowerCase() === query
      )
      .slice(0, 25);
  }, [allCountries, destInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSelection = (list: string[], setList: (l: string[]) => void, id: string) => {
    if (list.includes(id)) {
      setList(list.filter(item => item !== id));
    } else {
      setList([...list, id]);
    }
  };

  const handleSelectCountry = (country: { isoCode: string; name: string }) => {
    const destString = `${country.isoCode} ${country.name}`;
    const isAlreadySelected = destinations.some(
      (d) => d === destString || d.toLowerCase() === country.name.toLowerCase()
    );
    if (isAlreadySelected) return;

    if (destinations.length >= 5) {
      setDestError("You can select up to 5 destinations");
      return;
    }

    const updated = [...destinations, destString];
    setDestinations(updated);
    setDestInput("");
    setDestError("");
    if (updated.length >= 5) {
      setIsDropdownOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (destinations.length >= 5) {
        setDestError("You can select up to 5 destinations");
        return;
      }
      const firstAvailable = filteredCountries.find(
        (c) =>
          !destinations.some(
            (d) =>
              d.startsWith(`${c.isoCode} `) ||
              d.toLowerCase() === c.name.toLowerCase()
          )
      );
      if (firstAvailable) {
        handleSelectCountry(firstAvailable);
      }
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
    }
  };

  const removeDestination = (dest: string) => {
    setDestinations(destinations.filter(d => d !== dest));
    setDestError("");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (destinations.length === 0) {
      setDestError("Please select at least 1 favourite destination");
      return;
    } else if (destinations.length > 5) {
      setDestError("You can select up to 5 destinations");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      // 1. profileImg
      if (profileData?.profileImg instanceof File || profileData?.profileImg instanceof Blob) {
        formData.append("profileImg", profileData.profileImg);
      }

      // 2. Step 4 Basic Profile fields
      if (profileData?.firstName) formData.append("firstName", profileData.firstName);
      if (profileData?.lastName) formData.append("lastName", profileData.lastName);
      if (profileData?.username) formData.append("username", profileData.username);
      if (profileData?.DOB || profileData?.birthDate) {
        formData.append("DOB", formatDOBToYYYYMMDD(profileData.DOB || profileData.birthDate));
      }
      if (profileData?.headline) formData.append("headline", profileData.headline);
      if (profileData?.aboutMe) formData.append("aboutMe", profileData.aboutMe);
      if (profileData?.whatAreYouLookingFor || profileData?.lookingFor) {
        formData.append("whatAreYouLookingFor", profileData.whatAreYouLookingFor || profileData.lookingFor);
      }

      // 3. Step 4.1 More About You fields
      if (profileData?.hairColor) formData.append("hairColor", profileData.hairColor);
      if (profileData?.bodyType) formData.append("bodyType", profileData.bodyType);
      if (profileData?.gender) formData.append("gender", profileData.gender);
      if (profileData?.numberOfKids !== undefined && profileData?.numberOfKids !== "") {
        formData.append("numberOfKids", String(profileData.numberOfKids));
      }
      if (profileData?.country) formData.append("country", profileData.country);
      if (profileData?.state) formData.append("state", profileData.state);
      if (profileData?.city) formData.append("city", profileData.city);

      const langArray = Array.isArray(profileData?.language)
        ? profileData.language
        : Array.isArray(profileData?.languages)
          ? profileData.languages
          : [];
      if (langArray.length > 0) {
        formData.append("language", JSON.stringify(langArray));
      }

      let ethArray: string[] = [];
      const rawEth = profileData?.ethnicity || profileData?.ethnicities;
      if (Array.isArray(rawEth)) {
        ethArray = rawEth;
      } else if (typeof rawEth === "string" && rawEth.trim()) {
        try {
          const parsed = JSON.parse(rawEth);
          if (Array.isArray(parsed)) {
            ethArray = parsed;
          } else {
            ethArray = [rawEth.trim()];
          }
        } catch {
          ethArray = rawEth.split(",").map((s: string) => s.trim()).filter(Boolean);
        }
      }
      ethArray = ethArray.map((s: string) => String(s).trim()).filter(Boolean);
      if (ethArray.length > 0) {
        formData.append("ethnicity", JSON.stringify(ethArray));
      }
      if (profileData?.educationLevel || profileData?.education) {
        formData.append("educationLevel", profileData.educationLevel || profileData.education);
      }

      // 4. Step 4.2 Lifestyle Profile fields
      if (interest) formData.append("interests", interest);
      if (hobbies && hobbies.length > 0) formData.append("hobbies", JSON.stringify(hobbies));
      if (travel) formData.append("travelAvailability", JSON.stringify([travel]));

      if (destinations && destinations.length > 0) {
        const cleanDestinations = destinations.map((dest) => {
          const match = dest.match(/^([A-Za-z]{2})\s+(.*)$/);
          return match ? match[2].toLowerCase() : dest.toLowerCase();
        });
        formData.append("favouriteDestinations", JSON.stringify(cleanDestinations));
      }

      if (preferences && preferences.length > 0) {
        formData.append("lifeStylePreference", JSON.stringify(preferences));
      }

      const res: any = await editProfileDataV2API(formData);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        const updatedUser = resData?.user || resData?.data?.user || (resData?.id ? resData : null);
        if (updatedUser) {
          let existingUser = {};
          try {
            existingUser = JSON.parse(localStorage.getItem("user") || "{}");
          } catch (e) { }
          const merged = {
            ...existingUser,
            ...updatedUser,
            userType: updatedUser?.userType || {
              _id: USER_TYPE_IDS.CREATOR,
              title: "creator",
              description: "Creator",
            },
          };
          localStorage.setItem("user", JSON.stringify(merged));
          dispatch(setUser(merged));
        }
        showToast(resData?.message || "Profile updated successfully!");
        onNext();
      } else {
        showToast(resData?.message || "Failed to update profile. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update profile. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={parentStyles.formCard}>
      <h1 className={styles.formTitle}>Lifestyle Profile</h1>
      <p className={styles.formSubtitle}>
        This is what your audience will see. You can update it at any time from your dashboard.
      </p>

      <form onSubmit={onSubmit}>

        {/* Interests */}
        <div className={styles.section}>
          <label className={styles.label}>Interests <span>*</span></label>
          <div className={styles.cardsGrid}>

            <div className={`${styles.interestCard} ${interest === "women" ? styles.active : ""}`} onClick={() => setInterest("women")}>
              <span>Women</span>
              <div className={styles.interestIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                  <circle cx="12" cy="10" r="6" /><path d="M12 16v6M9 19h6" />
                </svg>
              </div>
            </div>

            <div className={`${styles.interestCard} ${interest === "men" ? styles.active : ""}`} onClick={() => setInterest("men")}>
              <span>Men</span>
              <div className={styles.interestIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                  <circle cx="10" cy="14" r="6" /><path d="M14 10l6-6M15 4h5v5" />
                </svg>
              </div>
            </div>

            <div className={`${styles.interestCard} ${interest === "non-binary" ? styles.active : ""}`} onClick={() => setInterest("non-binary")}>
              <span>Non-binary</span>
              <div className={styles.interestIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" /><path d="M12 7V3M10 5h4M7 17l-3 3M4 17v3h3M17 17l3 3M20 17v3h-3" />
                </svg>
              </div>
            </div>

            <div className={`${styles.interestCard} ${interest === "everyone" ? styles.active : ""}`} onClick={() => setInterest("everyone")}>
              <span>Everyone</span>
              <div className={styles.interestIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
            </div>

          </div>
        </div>

        {/* Hobbies */}
        <div className={styles.section}>
          <label className={styles.label}>Hobbies <span>*</span></label>
          <div className={styles.pillsContainer}>
            {HOBBIES.map(({ id, label, Icon }) => (
              <div
                key={id}
                className={`${styles.pill} ${hobbies.includes(id) ? styles.active : ""}`}
                onClick={() => toggleSelection(hobbies, setHobbies, id)}
              >
                <Icon size={14} className={styles.pillIcon} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Travel availability */}
        <div className={styles.section}>
          <label className={styles.label}>Travel availability <span>*</span></label>
          <div className={styles.pillsContainer}>
            {TRAVEL.map(({ id, label, Icon }) => (
              <div
                key={id}
                className={`${styles.pill} ${travel === id ? styles.active : ""}`}
                onClick={() => setTravel(id)}
              >
                <Icon size={14} className={styles.pillIcon} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Destinations */}
        <div className={styles.section}>
          <label className={styles.label}>Favourite Destinations <span>*</span></label>
          <div className={styles.searchWrapper} ref={dropdownRef}>
            <input
              type="text"
              placeholder={
                destinations.length >= 5
                  ? "Maximum 5 destinations selected"
                  : "Search"
              }
              className={styles.inputWithChevron}
              value={destInput}
              onFocus={() => {
                if (destinations.length < 5) {
                  setIsDropdownOpen(true);
                }
              }}
              onChange={(e) => {
                setDestInput(e.target.value);
                setIsDropdownOpen(true);
                if (destError) setDestError("");
              }}
              onKeyDown={handleKeyDown}
              disabled={destinations.length >= 5}
            />
            <button
              type="button"
              className={styles.chevronButton}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (destinations.length < 5) {
                  setIsDropdownOpen((prev) => !prev);
                }
              }}
              tabIndex={-1}
              aria-label="Toggle destinations dropdown"
            >
              <ChevronDown
                size={16}
                className={`${styles.inputChevron} ${
                  isDropdownOpen ? styles.inputChevronOpen : ""
                }`}
              />
            </button>

            {isDropdownOpen && destinations.length < 5 && (
              <div className={styles.dropdownList}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => {
                    const isSelected = destinations.some(
                      (d) =>
                        d.startsWith(`${c.isoCode} `) ||
                        d.toLowerCase() === c.name.toLowerCase()
                    );
                    return (
                      <div
                        key={c.isoCode}
                        className={`${styles.dropdownItem} ${
                          isSelected ? styles.dropdownItemSelected : ""
                        }`}
                        onClick={() => {
                          if (!isSelected) {
                            handleSelectCountry(c);
                          }
                        }}
                      >
                        <Flag
                          code={c.isoCode}
                          style={{
                            width: 20,
                            height: 14,
                            objectFit: "cover",
                            borderRadius: 2,
                          }}
                        />
                        <span>{c.name}</span>
                        {isSelected && (
                          <span
                            style={{
                              marginLeft: "auto",
                              fontSize: 12,
                              color: "#FE5A97",
                            }}
                          >
                            ✓ Added
                          </span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: "10px 14px",
                      color: "rgba(255,255,255,0.5)",
                      fontSize: 14,
                    }}
                  >
                    No country found
                  </div>
                )}
              </div>
            )}
          </div>
          {destError && <p className={parentStyles.errorText}>{destError}</p>}

          <div className={styles.destinationsContainer}>
            {destinations.map((dest) => {
              const match = dest.match(/^([A-Za-z]{2})\s+(.*)$/);
              const code = match ? match[1] : null;
              const label = match ? match[2] : dest;

              return (
                <div key={dest} className={styles.destinationPill}>
                  {code && (
                    <Flag
                      code={code}
                      style={{
                        width: 20,
                        height: 20,
                        objectFit: "cover",
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {label}
                  <button
                    type="button"
                    onClick={() => removeDestination(dest)}
                    title={`Remove ${label}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lifestyle preferences */}
        <div className={styles.section}>
          <label className={styles.label}>Lifestyle preferences</label>
          <div className={styles.pillsContainer}>
            {PREFERENCES.map(({ id, label, Icon }) => (
              <div
                key={id}
                className={`${styles.pill} ${preferences.includes(id) ? styles.active : ""}`}
                onClick={() => toggleSelection(preferences, setPreferences, id)}
              >
                <Icon size={14} className={styles.pillIcon} />
                {label}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Back not allowed", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Back not allowed" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            disabled={isSubmitting}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button type="submit" className={styles.continueBtn} disabled={isSubmitting}>
            {isSubmitting ? "Please wait..." : "Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
